import React, { useState, useEffect, useCallback } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { DEBT_TYPES, calcMortgage } from '../../../services/investmentPlanningService';
import { INTEREST_DEDUCTION_PURPOSES, defaultCollateralInterestPurpose } from '../../../services/planningTax/interestDeductibility';

const EMPTY = {
  name: '', type: '', entityId: '', currentBalance: '', interestRate: '5.5', monthlyPayment: '',
  maturityDate: '', renewalDate: '', amortizationRemaining: '25', notes: '', linkedAssetId: '',
  solveFor: 'payment', isCollateralLoan: false, isPfiLoan: false,
  interestDeductionPurpose: '',
};

const SOLVE_OPTIONS = [
  { value: 'payment', label: 'Versement mensuel' },
  { value: 'balance', label: 'Solde' },
  { value: 'amortization', label: 'Amortissement' },
];

const DebtsManager = ({ debts = [], entities = [], assets = [], onAdd, onUpdate, onDelete, formatCurrency }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [calculatedValue, setCalculatedValue] = useState('');

  const entityOptions = entities.map(e => ({ value: e.id, label: e.name }));
  const immobilierAssets = assets.filter(a => a.type === 'immobilier');
  const insuranceAssets = assets.filter(a => a.type === 'assurance_participative');
  const immobilierOptions = immobilierAssets.map(a => ({
    value: a.id,
    label: `${a.name} (${formatCurrency(a.currentBalance)})`,
  }));
  const insuranceOptions = insuranceAssets.map(a => ({
    value: a.id,
    label: `${a.name} (VR ${formatCurrency(a.currentBalance)})`,
  }));
  const linkedAsset = assets.find(a => a.id === form.linkedAssetId);
  const linkedToInsurance = linkedAsset?.type === 'assurance_participative';

  // Auto-calculate the missing field
  const autoCalc = useCallback((f) => {
    const { solveFor, currentBalance, interestRate, amortizationRemaining, monthlyPayment } = f;
    if (!solveFor) return;
    const result = calcMortgage(currentBalance, interestRate, amortizationRemaining, monthlyPayment, solveFor);
    if (result && isFinite(result) && result > 0) {
      setCalculatedValue(solveFor === 'amortization' ? Math.ceil(result).toString() : result.toFixed(2));
    } else {
      setCalculatedValue('');
    }
  }, []);

  useEffect(() => {
    if (showForm && (form.type === 'hypotheque' || form.type === 'pret_auto' || form.type === 'pret_personnel')) {
      autoCalc(form);
    }
  }, [form.currentBalance, form.interestRate, form.amortizationRemaining, form.monthlyPayment, form.solveFor, showForm, form.type, autoCalc]);

  const handleTypeChange = (type) => {
    setForm(prev => ({ ...EMPTY, entityId: prev.entityId, type, linkedAssetId: '' }));
    setCalculatedValue('');
  };

  const handleAssetLink = (assetId) => {
    const asset = immobilierAssets.find(a => a.id === assetId);
    setForm(prev => ({
      ...prev,
      linkedAssetId: assetId,
      name: asset ? `Hypothèque - ${asset.name}` : prev.name,
      entityId: prev.entityId || asset?.entityId || '',
    }));
  };

  const handleSave = () => {
    if (!form.type || !form.name.trim() || !form.entityId) return;
    if (form.type === 'hypotheque' && !form.linkedAssetId) return;

    // Apply calculated value to the solved field
    const data = { ...form };
    if (calculatedValue && form.solveFor) {
      if (form.solveFor === 'payment') data.monthlyPayment = calculatedValue;
      else if (form.solveFor === 'balance') data.currentBalance = calculatedValue;
      else if (form.solveFor === 'amortization') data.amortizationRemaining = calculatedValue;
    }

    const finalData = {
      ...data,
      currentBalance: parseFloat(data.currentBalance) || 0,
      interestRate: parseFloat(data.interestRate) || 0,
      monthlyPayment: parseFloat(data.monthlyPayment) || 0,
      amortizationRemaining: parseFloat(data.amortizationRemaining) || 0,
    };

    if (editingId) { onUpdate(editingId, finalData); }
    else { onAdd(finalData); }
    setForm({ ...EMPTY, entityId: entities[0]?.id || '' });
    setShowForm(false);
    setEditingId(null);
    setCalculatedValue('');
  };

  const handleEdit = (item) => {
    setForm({
      name: item.name || '', type: item.type || 'hypotheque', entityId: item.entityId || '',
      currentBalance: String(item.currentBalance || ''), interestRate: String(item.interestRate || '5.5'),
      monthlyPayment: String(item.monthlyPayment || ''), maturityDate: item.maturityDate || '',
      renewalDate: item.renewalDate || '', amortizationRemaining: String(item.amortizationRemaining || '25'),
      notes: item.notes || '', linkedAssetId: item.linkedAssetId || '', solveFor: 'payment',
      isCollateralLoan: !!item.isCollateralLoan,
      isPfiLoan: !!item.isPfiLoan,
      interestDeductionPurpose: item.interestDeductionPurpose || '',
    });
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleCancel = () => {
    setForm({ ...EMPTY, entityId: entities[0]?.id || '' });
    setShowForm(false);
    setEditingId(null);
    setCalculatedValue('');
  };

  const openForm = () => {
    setForm({ ...EMPTY, entityId: entities[0]?.id || '' });
    setEditingId(null);
    setShowForm(true);
    setCalculatedValue('');
  };

  const getEntityName = (id) => entities.find(e => e.id === id)?.name || '—';
  const getTypeName = (type) => DEBT_TYPES.find(t => t.value === type)?.label || type;
  const getAssetName = (id) => assets.find(a => a.id === id)?.name || '—';
  const totalBalance = debts.reduce((s, d) => s + (parseFloat(d.currentBalance) || 0), 0);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try { return new Date(dateStr).toLocaleDateString('fr-CA', { year: 'numeric', month: 'short' }); }
    catch { return dateStr; }
  };

  const hasCalc = ['hypotheque', 'pret_auto', 'pret_personnel'].includes(form.type);

  const renderTypeFields = () => {
    if (!form.type) return null;
    const isHypotheque = form.type === 'hypotheque';

    return (
      <>
        {isHypotheque && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Select label="Immeuble associé" value={form.linkedAssetId} onChange={handleAssetLink}
              options={immobilierOptions}
              placeholder={immobilierAssets.length === 0 ? "Ajoutez d'abord un actif immobilier..." : "Sélectionner l'immeuble..."}
              required data-testid="debt-asset-select" />
            <Select label="Détenteur" value={form.entityId} onChange={v => setForm({ ...form, entityId: v })}
              options={entityOptions} placeholder="Sélectionner..." required />
          </div>
        )}

        {isHypotheque && immobilierAssets.length === 0 && (
          <div className="flex items-center gap-2 p-3 rounded-lg border border-primary/20 bg-primary/5 text-sm text-primary">
            <Icon name="Info" size={16} />
            <span>Ajoutez d'abord un actif immobilier dans la section Actifs ci-dessus.</span>
          </div>
        )}

        {!isHypotheque && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input label="Nom" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
              placeholder={getDebtPlaceholder(form.type)} required data-testid="debt-name-input" />
            <Select label="Détenteur" value={form.entityId} onChange={v => setForm({ ...form, entityId: v })}
              options={entityOptions} placeholder="Sélectionner..." required />
          </div>
        )}

        {isHypotheque && form.linkedAssetId && (
          <Input label="Nom" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="debt-name-input" />
        )}

        {/* Auto-calc selector */}
        {hasCalc && (
          <div className="flex items-center gap-3 p-3 rounded-lg border border-primary/20 bg-primary/5" data-testid="auto-calc-section">
            <Icon name="Calculator" size={16} className="text-primary shrink-0" />
            <span className="text-sm text-foreground font-medium whitespace-nowrap">Calculer :</span>
            <Select
              value={form.solveFor}
              onChange={v => setForm({ ...form, solveFor: v })}
              options={SOLVE_OPTIONS}
              data-testid="solve-for-select"
            />
            {calculatedValue && (
              <span className="text-sm font-semibold text-primary whitespace-nowrap" style={{ fontFamily: 'var(--font-data)' }} data-testid="calculated-value">
                = {form.solveFor === 'amortization' ? `${calculatedValue} ans` : `${formatCurrency(parseFloat(calculatedValue))}`}
              </span>
            )}
          </div>
        )}

        <div className={`grid grid-cols-1 gap-3 ${isHypotheque ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
          <Input
            label={`Solde actuel ($)${form.solveFor === 'balance' ? ' (calculé)' : ''}`}
            type="number"
            value={form.solveFor === 'balance' ? calculatedValue : form.currentBalance}
            onChange={e => setForm({ ...form, currentBalance: e.target.value })}
            placeholder="876 000"
            disabled={form.solveFor === 'balance' && hasCalc}
            style={form.solveFor === 'balance' && hasCalc ? { opacity: 0.7, background: 'var(--color-primary-light, rgba(59,130,246,0.05))' } : {}}
          />
          <Input label="Taux d'intérêt (%)" type="number" value={form.interestRate}
            onChange={e => setForm({ ...form, interestRate: e.target.value })} placeholder="5.5" />
          <Input
            label={`Paiement mensuel ($)${form.solveFor === 'payment' ? ' (calculé)' : ''}`}
            type="number"
            value={form.solveFor === 'payment' ? calculatedValue : form.monthlyPayment}
            onChange={e => setForm({ ...form, monthlyPayment: e.target.value })}
            placeholder="4 973"
            disabled={form.solveFor === 'payment' && hasCalc}
            style={form.solveFor === 'payment' && hasCalc ? { opacity: 0.7, background: 'var(--color-primary-light, rgba(59,130,246,0.05))' } : {}}
          />
          {isHypotheque && (
            <Input
              label={`Amortissement (ans)${form.solveFor === 'amortization' ? ' (calculé)' : ''}`}
              type="number"
              value={form.solveFor === 'amortization' ? calculatedValue : form.amortizationRemaining}
              onChange={e => setForm({ ...form, amortizationRemaining: e.target.value })}
              placeholder="25"
              disabled={form.solveFor === 'amortization' && hasCalc}
              style={form.solveFor === 'amortization' && hasCalc ? { opacity: 0.7, background: 'var(--color-primary-light, rgba(59,130,246,0.05))' } : {}}
            />
          )}
        </div>

        {!isHypotheque && insuranceAssets.length > 0 && (
          <Select
            label="Police / actif lié (optionnel)"
            value={form.linkedAssetId}
            onChange={(v) => {
              const asset = insuranceAssets.find((a) => a.id === v);
              setForm({
                ...form,
                linkedAssetId: v,
                isPfiLoan: false,
                interestDeductionPurpose: v
                  ? defaultCollateralInterestPurpose({ isCollateralLoan: true })
                  : '',
              });
            }}
            options={[{ value: '', label: 'Aucun' }, ...insuranceOptions]}
            placeholder="Lier à une police..."
            data-testid="debt-insurance-link-select"
          />
        )}

        {linkedToInsurance && (
          <div className="flex flex-wrap gap-4 p-3 rounded-lg border border-primary/20 bg-primary/5 text-sm" data-testid="debt-collateral-flags">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.isCollateralLoan}
                onChange={(e) => setForm({ ...form, isCollateralLoan: e.target.checked })}
              />
              Prêt collatéral (banque)
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.isPfiLoan}
                onChange={(e) => setForm({
                  ...form,
                  isPfiLoan: e.target.checked,
                  isCollateralLoan: e.target.checked ? true : form.isCollateralLoan,
                })}
              />
              Prêt PFI
            </label>
            <div className="w-full basis-full">
              <Select
                label="Déductibilité des intérêts"
                value={form.interestDeductionPurpose || defaultCollateralInterestPurpose(form)}
                onChange={(v) => setForm({ ...form, interestDeductionPurpose: v })}
                options={[
                  { value: INTEREST_DEDUCTION_PURPOSES.MISE_DE_FONDS_IMMEUBLE, label: 'Mise de fonds / immeuble à revenus' },
                  { value: INTEREST_DEDUCTION_PURPOSES.REVENU_PLACEMENT, label: 'Revenu de placement (PFI, levier)' },
                  { value: INTEREST_DEDUCTION_PURPOSES.REVENU_LOCATION, label: 'Revenu locatif' },
                  { value: INTEREST_DEDUCTION_PURPOSES.PRIMES_ASSURANCE, label: 'Primes assurance seulement (non déductible)' },
                ]}
                data-testid="debt-interest-deduction-purpose"
              />
              <p className="text-[0.65rem] text-muted-foreground mt-1">
                Réf. LIR : intérêts déductibles si le prêt finance un revenu non exonéré (PFI, collatéral pour immeuble). Non déductibles si le prêt sert uniquement aux primes.
              </p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input label="Date d'échéance" type="date" value={form.maturityDate} onChange={e => setForm({ ...form, maturityDate: e.target.value })} />
          <Input label="Date de renouvellement" type="date" value={form.renewalDate} onChange={e => setForm({ ...form, renewalDate: e.target.value })} />
        </div>
      </>
    );
  };

  return (
    <div className="bg-card border border-border rounded-xl p-5 md:p-6" data-testid="debts-manager">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-foreground flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
            <Icon name="CreditCard" size={20} style={{ color: 'var(--color-destructive)' }} />
            Dettes
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">Total: <span className="font-semibold" style={{ fontFamily: 'var(--font-data)' }}>{formatCurrency(totalBalance)}</span></p>
        </div>
        <Button size="sm" iconName="Plus" iconPosition="left" onClick={openForm} data-testid="add-debt-btn">Ajouter</Button>
      </div>

      {showForm && (
        <div className="bg-muted rounded-lg p-4 mb-4 space-y-3" data-testid="debt-form">
          <Select label="Type de dette" value={form.type} onChange={handleTypeChange} options={DEBT_TYPES}
            placeholder="Choisir le type de dette..." required data-testid="debt-type-select" />
          {renderTypeFields()}
          {form.type && (
            <>
              <Input label="Notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Optionnel" />
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSave} data-testid="debt-save-btn">{editingId ? 'Mettre à jour' : 'Ajouter'}</Button>
                <Button size="sm" variant="outline" onClick={handleCancel} data-testid="debt-cancel-btn">Annuler</Button>
              </div>
            </>
          )}
        </div>
      )}

      {debts.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-6">Aucune dette enregistrée.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="debts-table">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-border">
                <th className="pb-2 pr-3" style={{ fontFamily: 'var(--font-caption)' }}>Nom</th>
                <th className="pb-2 pr-3" style={{ fontFamily: 'var(--font-caption)' }}>Type</th>
                <th className="pb-2 pr-3" style={{ fontFamily: 'var(--font-caption)' }}>Détenteur</th>
                <th className="pb-2 pr-3 text-right" style={{ fontFamily: 'var(--font-caption)' }}>Solde</th>
                <th className="pb-2 pr-3 text-right" style={{ fontFamily: 'var(--font-caption)' }}>Taux</th>
                <th className="pb-2 pr-3 text-right" style={{ fontFamily: 'var(--font-caption)' }}>Paiement/mois</th>
                <th className="pb-2 pr-3" style={{ fontFamily: 'var(--font-caption)' }}>Renouvellement</th>
                <th className="pb-2 w-20"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {debts.map(item => (
                <tr key={item.id} className="hover:bg-muted/30 transition-colors" data-testid={`debt-row-${item.id}`}>
                  <td className="py-2.5 pr-3">
                    <div className="flex items-center gap-2">
                      {item.type === 'hypotheque' && <Icon name="Home" size={14} className="text-primary shrink-0" />}
                      <span className="font-medium text-foreground">{item.name}</span>
                    </div>
                    {item.linkedAssetId && <p className="text-xs text-muted-foreground mt-0.5">Lié: {getAssetName(item.linkedAssetId)}</p>}
                    {(item.isCollateralLoan || item.isPfiLoan) && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {item.isCollateralLoan && (
                          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-primary/10 text-primary">Collatéral</span>
                        )}
                        {item.isPfiLoan && (
                          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-800">PFI</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="py-2.5 pr-3 text-muted-foreground">{getTypeName(item.type)}</td>
                  <td className="py-2.5 pr-3 text-muted-foreground">{getEntityName(item.entityId)}</td>
                  <td className="py-2.5 pr-3 text-right font-medium" style={{ fontFamily: 'var(--font-data)', color: 'var(--color-destructive)' }}>{formatCurrency(item.currentBalance)}</td>
                  <td className="py-2.5 pr-3 text-right" style={{ fontFamily: 'var(--font-data)' }}>{item.interestRate}%</td>
                  <td className="py-2.5 pr-3 text-right" style={{ fontFamily: 'var(--font-data)' }}>{formatCurrency(item.monthlyPayment)}</td>
                  <td className="py-2.5 pr-3 text-muted-foreground">{formatDate(item.renewalDate)}</td>
                  <td className="py-2.5">
                    <div className="flex gap-1 justify-end">
                      <Button size="xs" variant="ghost" iconName="Pencil" onClick={() => handleEdit(item)} />
                      <Button size="xs" variant="ghost" iconName="Trash2" onClick={() => onDelete(item.id)} style={{ color: 'var(--color-destructive)' }} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

function getDebtPlaceholder(type) {
  const p = { marge_credit: 'Ex: Marge Desjardins', pret_auto: 'Ex: Prêt auto Toyota', pret_personnel: 'Ex: Prêt BNC', carte_credit: 'Ex: Visa Infinite TD', autre: 'Ex: Prêt privé' };
  return p[type] || 'Nom de la dette';
}

export default DebtsManager;
